import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Play, Ban, Pause, MoreVertical } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  getActiveWorkout, 
  setActiveWorkout, 
  clearActiveWorkout,
  type ActiveWorkout 
} from "@/lib/activeWorkoutStore";

export { getActiveWorkout, setActiveWorkout, clearActiveWorkout, type ActiveWorkout };

function formatElapsed(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function ResumeWorkoutBar() {
  const [location, setLocation] = useLocation();
  const [activeWorkout, setActiveWorkoutState] = useState<ActiveWorkout | null>(null);
  const [currentElapsed, setCurrentElapsed] = useState(0);
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  useEffect(() => {
    const checkActiveWorkout = () => {
      const workout = getActiveWorkout();
      setActiveWorkoutState(workout);
      if (workout && !workout.isPaused) {
        // Use unified formula: accumulated elapsed + live duration
        const now = Date.now();
        const liveDuration = Math.floor((now - workout.startedAt) / 1000);
        setCurrentElapsed(workout.elapsed + liveDuration);
      } else if (workout) {
        setCurrentElapsed(workout.elapsed);
      }
    };

    checkActiveWorkout();
    const interval = setInterval(checkActiveWorkout, 1000);
    
    return () => clearInterval(interval);
  }, [location]);

  useEffect(() => {
    if (!activeWorkout || activeWorkout.isPaused) return;
    
    const interval = setInterval(() => {
      // Use unified formula: accumulated elapsed + live duration
      const now = Date.now();
      const liveDuration = Math.floor((now - activeWorkout.startedAt) / 1000);
      setCurrentElapsed(activeWorkout.elapsed + liveDuration);
    }, 1000);
    
    return () => clearInterval(interval);
  }, [activeWorkout]);

  const isOnWorkoutPage = location.startsWith('/program/');
  const isOnHomePage = location === '/' || location === '';
  
  if (!activeWorkout || isOnWorkoutPage || isOnHomePage) {
    return null;
  }

  const handleResume = () => {
    setLocation(`/program/${activeWorkout.programId}?week=${activeWorkout.week}&workout=${activeWorkout.workoutIndex}`);
  };

  const handleDismiss = () => {
    setShowCancelDialog(true);
  };

  const confirmCancelWorkout = () => {
    clearActiveWorkout();
    setActiveWorkoutState(null);
    setShowCancelDialog(false);
  };

  const handlePauseToggle = () => {
    if (!activeWorkout) return;
    
    const now = Date.now();
    if (activeWorkout.isPaused) {
      // Resuming - reset startedAt, keep elapsed as accumulated
      const updated = {
        ...activeWorkout,
        startedAt: now,
        isPaused: false
      };
      setActiveWorkout(updated);
      setActiveWorkoutState(updated);
    } else {
      // Pausing - save total elapsed time
      const liveDuration = Math.floor((now - activeWorkout.startedAt) / 1000);
      const updated = {
        ...activeWorkout,
        elapsed: activeWorkout.elapsed + liveDuration,
        isPaused: true
      };
      setActiveWorkout(updated);
      setActiveWorkoutState(updated);
    }
  };

  return (
    <>
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent className="bg-card border-border/50">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground flex items-center gap-2">
              <Ban className="w-5 h-5 text-red-400" />
              Cancel Workout?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              Are you sure you want to cancel this workout? Your progress will not be saved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-9">Keep Working Out</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmCancelWorkout}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="button-confirm-cancel-workout-bar"
            >
              Cancel Workout
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        className="fixed bottom-0 left-0 right-0 z-50 p-4 pb-6"
      >
        <div 
          onClick={handleResume}
          className={cn(
            "max-w-md mx-auto rounded-2xl shadow-lg overflow-hidden cursor-pointer hover:shadow-xl transition-all group relative",
            activeWorkout.isPaused
              ? "bg-gradient-to-r from-amber-400/90 to-orange-400/90 dark:from-amber-500/70 dark:to-orange-400/60"
              : "bg-gradient-to-r from-primary/90 to-emerald-600/90 dark:from-primary/80 dark:to-emerald-600/70"
          )}
        >
          <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
          
          <div className="flex items-center gap-3 p-3 relative">
            {/* Play/Pause button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handlePauseToggle();
              }}
              className="w-12 h-12 rounded-full bg-white/40 dark:bg-white/30 backdrop-blur-sm flex items-center justify-center shrink-0 hover:scale-110 hover:bg-white/50 dark:hover:bg-white/40 transition-all shadow-sm"
              data-testid="button-pause-workout"
            >
              {activeWorkout.isPaused ? (
                <Play className="w-6 h-6 text-white fill-white ml-0.5" />
              ) : (
                <Pause className="w-6 h-6 text-white fill-white" />
              )}
            </button>
            
            {/* Workout info */}
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-wider text-white/60 leading-none mb-0.5">
                {activeWorkout.isPaused ? "Paused" : "In Progress"} • {formatElapsed(currentElapsed)}
              </p>
              <p className="text-base font-bold text-white truncate leading-tight">
                {activeWorkout.workoutName}
              </p>
            </div>
            
            {/* Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-white/60 hover:text-white hover:bg-white/20 shrink-0"
                  data-testid="button-workout-menu-bar"
                >
                  <MoreVertical className="w-5 h-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-card border-border/50">
                <DropdownMenuItem
                  className="text-red-400 focus:text-red-400 focus:bg-red-400/10"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDismiss();
                  }}
                  data-testid="button-cancel-workout-bar"
                >
                  <Ban className="w-4 h-4 mr-2" />
                  Cancel Workout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
    </>
  );
}
